import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CartItem, CartRegion, Product, ProductVariant, Region } from '../types';
import { api } from '../services/api';
import { positionCaption } from '../utils/sharing';

// SH — полка шеринга: позиции на общих аккаунтах, цены в рублях без курса
type ItemsByRegion = Record<CartRegion, CartItem[]>;
const CART_REGIONS: CartRegion[] = ['TR', 'IN', 'UA', 'SH'];

const EMPTY_BY_REGION: ItemsByRegion = { TR: [], IN: [], UA: [], SH: [] };

function groupByRegion(items: CartItem[]): ItemsByRegion {
  const out: ItemsByRegion = { TR: [], IN: [], UA: [], SH: [] };
  for (const it of items) {
    const r = it.variant?.region as CartRegion | undefined;
    if (r && out[r]) out[r].push(it);
  }
  return out;
}

interface CartStore {
  itemsByRegion: ItemsByRegion;
  isLoading: boolean;
  isSynced: boolean;

  email: string;
  needNewAccount: boolean;
  agreedToTerms: boolean;
  promoCode: string;
  saveData: boolean;
  accountTarget: 'own' | 'new';
  accountEmail: string;
  accountPassword: string;

  loadCart: () => Promise<void>;
  refreshStaleItems: () => Promise<void>;
  /** Сверяет полку шеринга с сервером: убирает недоступные позиции, обновляет цены */
  refreshSharingItems: () => Promise<{ removed: string[]; repriced: boolean }>;
  addItem: (product: Product, variant: ProductVariant) => Promise<boolean>;
  removeItem: (variantId: string) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  clearCart: () => void;
  clearRegionCart: (region: CartRegion) => void;
  setEmail: (email: string) => void;
  setNeedNewAccount: (value: boolean) => void;
  setAgreedToTerms: (value: boolean) => void;
  setPromoCode: (code: string) => void;
  setSaveData: (value: boolean) => void;
  setAccountTarget: (value: 'own' | 'new') => void;
  setAccountEmail: (email: string) => void;
  setAccountPassword: (password: string) => void;
  getItemsForRegion: (region: Region) => CartItem[];
  getTotalAmountForRegion: (region: Region) => number;
  getTotalCountForRegion: (region: Region) => number;
  getAllItems: () => CartItem[];
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      itemsByRegion: { ...EMPTY_BY_REGION },
      isLoading: false,
      isSynced: false,

      email: '',
      needNewAccount: false,
      agreedToTerms: true,
      promoCode: '',
      saveData: false,
      accountTarget: 'own',
      accountEmail: '',
      accountPassword: '',

      loadCart: async () => {
        set({ isLoading: true });
        try {
          const serverItems = await api.getCart();
          set({ itemsByRegion: groupByRegion(serverItems), isSynced: true });
        } catch {
          // Keep local items if server fails
        } finally {
          set({ isLoading: false });
        }
      },

      // Лечит устаревшие снапшоты в корзине: у старых записей может не быть
      // variant.regionalPrice (или свежих цен/override) — перезагружаем такие
      // товары с сервера точечно, не трогая остальное содержимое корзины.
      refreshStaleItems: async () => {
        const byRegion = get().itemsByRegion;
        const stale: CartItem[] = [];
        for (const r of ['TR', 'IN', 'UA'] as Region[]) {
          for (const item of byRegion[r] || []) {
            if (!item.variant.regionalPrice || item.variant.regionalPrice <= 0) stale.push(item);
          }
        }
        if (stale.length === 0) return;

        for (const staleItem of stale) {
          try {
            const fresh = await api.getProduct(staleItem.product.id);
            const freshVariant = fresh?.variants?.find((v) => v.id === staleItem.variant.id);
            if (!fresh || !freshVariant) continue;
            set((state) => {
              const next: ItemsByRegion = { ...EMPTY_BY_REGION, SH: state.itemsByRegion.SH || [] };
              for (const r of ['TR', 'IN', 'UA'] as Region[]) {
                next[r] = (state.itemsByRegion[r] || []).map((i) =>
                  i.variant.id === staleItem.variant.id
                    ? { ...i, product: fresh, variant: freshVariant }
                    : i
                );
              }
              return { itemsByRegion: next };
            });
          } catch {
            /* сервер недоступен — оставляем как есть */
          }
        }
      },

      // Позиции шеринга выключают вручную, а цену могут поменять, пока товар лежит в корзине:
      // при каждом открытии корзины сверяем полку SH с сервером. Скрытый или архивный товар
      // сервер больше не отдает — такая строка уходит из корзины.
      refreshSharingItems: async () => {
        const items = get().itemsByRegion.SH || [];
        if (items.length === 0) return { removed: [], repriced: false };
        const fresh = new Map<string, Product | null>();
        for (const item of items) {
          if (!fresh.has(item.product.id)) fresh.set(item.product.id, await api.getProduct(item.product.id));
        }
        const removed: string[] = [];
        let repriced = false;
        const next = (get().itemsByRegion.SH || []).flatMap((i) => {
          const product = fresh.get(i.product.id);
          const variant = product?.variants?.find((v) => v.id === i.variant.id);
          if (!product || !variant || !variant.available || variant.price <= 0) {
            removed.push(`${i.product.name} · ${positionCaption(i.product, i.variant)}`);
            return [];
          }
          if (variant.price !== i.variant.price) repriced = true;
          return [{ ...i, product, variant }];
        });
        if (removed.length > 0 || repriced) {
          set((state) => ({ itemsByRegion: { ...state.itemsByRegion, SH: next } }));
        }
        return { removed, repriced };
      },

      addItem: async (product, variant) => {
        const region = variant.region as CartRegion;
        const current = get().itemsByRegion[region] || [];
        const existing = current.find((item) => item.variant.id === variant.id);
        const newQuantity = existing ? existing.quantity + 1 : 1;

        const updated = existing
          ? current.map((item) =>
              item.variant.id === variant.id ? { ...item, quantity: newQuantity } : item
            )
          : [...current, { product, variant, quantity: 1 }];

        set({ itemsByRegion: { ...get().itemsByRegion, [region]: updated } });

        api.addToCart(product.id, variant.id, newQuantity).catch(console.error);
        return true;
      },

      removeItem: async (variantId) => {
        const byRegion = get().itemsByRegion;
        const next: ItemsByRegion = { ...EMPTY_BY_REGION };
        for (const r of CART_REGIONS) {
          next[r] = (byRegion[r] || []).filter((i) => i.variant.id !== variantId);
        }
        set({ itemsByRegion: next });
        api.removeFromCart(variantId).catch(console.error);
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }

        const byRegion = get().itemsByRegion;
        let foundProductId: string | null = null;
        const next: ItemsByRegion = { ...EMPTY_BY_REGION };
        for (const r of CART_REGIONS) {
          const bucket = byRegion[r] || [];
          next[r] = bucket.map((i) => {
            if (i.variant.id === variantId) {
              foundProductId = i.product.id;
              return { ...i, quantity };
            }
            return i;
          });
        }
        if (!foundProductId) return;
        set({ itemsByRegion: next });
        api.addToCart(foundProductId, variantId, quantity).catch(console.error);
      },

      clearCart: () => {
        const saveData = get().saveData;
        const email = get().email;
        const accountEmail = get().accountEmail;
        const accountPassword = get().accountPassword;
        set({
          itemsByRegion: { ...EMPTY_BY_REGION },
          email: saveData ? email : '',
          needNewAccount: false,
          agreedToTerms: true,
          promoCode: '',
          accountEmail: saveData ? accountEmail : '',
          accountPassword: saveData ? accountPassword : '',
        });
      },

      clearRegionCart: (region) => {
        set({ itemsByRegion: { ...get().itemsByRegion, [region]: [] } });
      },

      setEmail: (email) => set({ email }),
      setNeedNewAccount: (value) => set({ needNewAccount: value }),
      setAgreedToTerms: (value) => set({ agreedToTerms: value }),
      setPromoCode: (code) => set({ promoCode: code }),
      setSaveData: (value) => set({ saveData: value }),
      setAccountTarget: (value) => set({ accountTarget: value }),
      setAccountEmail: (email) => set({ accountEmail: email }),
      setAccountPassword: (password) => set({ accountPassword: password }),

      getItemsForRegion: (region) => get().itemsByRegion[region] || [],

      getTotalAmountForRegion: (region) =>
        (get().itemsByRegion[region] || []).reduce(
          (total, item) => total + item.variant.price * item.quantity,
          0
        ),

      getTotalCountForRegion: (region) =>
        (get().itemsByRegion[region] || []).reduce((sum, item) => sum + item.quantity, 0),

      getAllItems: () => {
        const byRegion = get().itemsByRegion;
        return [...(byRegion.TR || []), ...(byRegion.IN || []), ...(byRegion.UA || []), ...(byRegion.SH || [])];
      },
    }),
    {
      name: 'cart-storage',
      version: 4,
      partialize: (state) => ({
        itemsByRegion: state.itemsByRegion,
        email: state.email,
        saveData: state.saveData,
        needNewAccount: state.needNewAccount,
        agreedToTerms: state.agreedToTerms,
        promoCode: state.promoCode,
        accountTarget: state.accountTarget,
        accountEmail: state.saveData ? state.accountEmail : '',
        accountPassword: state.saveData ? state.accountPassword : '',
      }),
      migrate: (persisted: any, version: number) => {
        if (version < 2 && persisted && Array.isArray(persisted.items)) {
          const itemsByRegion = groupByRegion(persisted.items);
          const { items: _items, ...rest } = persisted;
          persisted = { ...rest, itemsByRegion };
        }
        // v3: промокод стирается после заказа. Старые версии его не стирали —
        // разово убираем то, что осталось висеть в корзине
        if (version < 3 && persisted) {
          persisted = { ...persisted, promoCode: '' };
        }
        // v4: появилась полка шеринга (SH)
        if (version < 4 && persisted?.itemsByRegion && !persisted.itemsByRegion.SH) {
          persisted = { ...persisted, itemsByRegion: { ...persisted.itemsByRegion, SH: [] } };
        }
        return persisted;
      },
    }
  )
);
